/*
 * Copyright (c) 2025 Hardfin, Inc.
 */
import { Link } from '@/components/link';
import {
  AddLinkOutlined,
  LocalOfferOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Ticket } from '@/ticket/api/types';
import { TicketAssetsDialog } from '@/ticket/components/dialogs/ticket-assets-dialog';
import {
  Box,
  Button,
  Card,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

interface Props {
  ticket: Ticket;
}

export function TicketDetailAssetsAttached(props: Props) {
  const dialog = useDisclosure(false);
  const ticket = props.ticket;
  return (
    <Card variant="outlined" sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Assets</Typography>
          <TicketAssetsDialog
            ticket={ticket}
            isOpen={dialog.isOpen}
            onClose={dialog.close}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddLinkOutlined />}
            onClick={() => dialog.open()}
          >
            Attach
          </Button>
        </Box>
        {ticket.assets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assets attached
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {ticket.assets.map((asset) => (
              <ListItem
                key={asset.key}
                sx={{
                  py: 0.5,
                  width: '50%', // Makes each item take up half the width
                  minWidth: 'fit-content',
                  textOverflow: 'ellipsis',
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <LocalOfferOutlined fontSize="small" color="action" />
                </ListItemIcon>
                <Link
                  to="/assets/$assetKey"
                  params={{ assetKey: asset.key }}
                  color="primary.main"
                  variant="body2"
                >
                  <ListItemText
                    primary={`${asset.serial}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </Link>
              </ListItem>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}
